import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faChevronLeft,
  faChevronRight,
  faSearchPlus,
  faSearchMinus,
  faExpand,
  faCompress,
  faDownload,
  faCheck,
  faCheckDouble,
  faLayerGroup,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import '../styles/ImageViewer.css';

interface ImageViewerProps {
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  onIndexChange?: (index: number) => void;
}

const ImageViewer: React.FC<ImageViewerProps> = ({
  images,
  currentIndex,
  onClose,
  onNext,
  onPrev,
  onIndexChange,
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState<Set<number>>(new Set());
  const [isDownloading, setIsDownloading] = useState(false);
  
  const overlayRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const lastPosition = useRef({ x: 0, y: 0 });

  const currentImage = images[currentIndex];
  const isCurrentSelected = selectedImages.has(currentIndex);
  const selectedCount = selectedImages.size;

  // Prevent all scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // Reset on image change
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsLoading(true);
  }, [currentIndex]);

  // Fullscreen listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Keyboard
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullscreen) {
          document.exitFullscreen();
        } else if (isSelectionMode) {
          setIsSelectionMode(false);
          setSelectedImages(new Set());
        } else {
          onClose();
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onNext();
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        if (!isSelectionMode) {
          setIsSelectionMode(true);
          setSelectedImages(new Set([currentIndex]));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, onPrev, onNext, currentIndex, isFullscreen, isSelectionMode]);

  const zoomIn = useCallback(() => {
    setScale(prev => Math.min(prev + 0.5, 5));
  }, []);

  const zoomOut = useCallback(() => {
    setScale(prev => {
      const newScale = Math.max(prev - 0.5, 1);
      if (newScale === 1) setPosition({ x: 0, y: 0 });
      return newScale;
    });
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await overlayRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  }, []);

  const downloadImage = useCallback(async (imageUrl: string, filename: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(imageUrl, '_blank');
    }
  }, []);

  const handleDownloadCurrent = useCallback(() => {
    downloadImage(currentImage, `image-${currentIndex + 1}.jpg`);
  }, [currentImage, currentIndex, downloadImage]);

  const handleDownloadSelected = useCallback(async () => {
    if (selectedCount === 0) return;
    setIsDownloading(true);
    const indices = Array.from(selectedImages).sort((a, b) => a - b);
    for (let i = 0; i < indices.length; i++) {
      await downloadImage(images[indices[i]], `image-${indices[i] + 1}.jpg`);
      if (i < indices.length - 1) await new Promise(r => setTimeout(r, 350));
    }
    setIsDownloading(false);
  }, [selectedImages, selectedCount, images, downloadImage]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (scale <= 1) return;
    e.preventDefault();
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    lastPosition.current = { ...position };
  }, [scale, position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current || scale <= 1) return;
    setPosition({
      x: lastPosition.current.x + (e.clientX - dragStart.current.x),
      y: lastPosition.current.y + (e.clientY - dragStart.current.y),
    });
  }, [scale]);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const goToImage = useCallback((index: number) => {
    if (onIndexChange) {
      onIndexChange(index);
    }
  }, [onIndexChange]);

  const toggleSelection = useCallback((index: number) => {
    setSelectedImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedImages(new Set(images.map((_, i) => i)));
  }, [images]);

  const clearSelection = useCallback(() => {
    setSelectedImages(new Set());
  }, []);

  // Prevent default on ALL buttons to stop focus scroll
  const preventFocus = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  return (
    <div ref={overlayRef} className="imgv-overlay">
      {/* Header */}
      <div className="imgv-header">
        <div className="imgv-counter">
          {currentIndex + 1} / {images.length}
          {isSelectionMode && selectedCount > 0 && (
            <span className="imgv-badge">{selectedCount} selected</span>
          )}
        </div>
        
        <div className="imgv-controls">
          {images.length > 1 && !isSelectionMode && (
            <button className="imgv-btn" onMouseDown={preventFocus} onClick={() => setIsSelectionMode(true)}>
              <FontAwesomeIcon icon={faLayerGroup} />
            </button>
          )}
          
          <button className="imgv-btn" onMouseDown={preventFocus} onClick={zoomOut} disabled={scale <= 1}>
            <FontAwesomeIcon icon={faSearchMinus} />
          </button>
          <span className="imgv-zoom">{Math.round(scale * 100)}%</span>
          <button className="imgv-btn" onMouseDown={preventFocus} onClick={zoomIn} disabled={scale >= 5}>
            <FontAwesomeIcon icon={faSearchPlus} />
          </button>
          
          <button className="imgv-btn" onMouseDown={preventFocus} onClick={toggleFullscreen}>
            <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
          </button>
          
          <button className="imgv-btn" onMouseDown={preventFocus} onClick={handleDownloadCurrent}>
            <FontAwesomeIcon icon={faDownload} />
          </button>
          
          <button className="imgv-btn imgv-btn-close" onMouseDown={preventFocus} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      {/* Selection bar */}
      {isSelectionMode && (
        <div className="imgv-selbar">
          <div className="imgv-selbar-left">
            <button className="imgv-selbtn-exit" onMouseDown={preventFocus} onClick={() => { setIsSelectionMode(false); setSelectedImages(new Set()); }}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <span className="imgv-sel-title">Select Images</span>
          </div>
          
          <div className="imgv-selbar-actions">
            <button className="imgv-selbtn" onMouseDown={preventFocus} onClick={selectAll}>
              <FontAwesomeIcon icon={faCheckDouble} />
              <span>All</span>
            </button>
            
            {selectedCount > 0 && (
              <>
                <button className="imgv-selbtn" onMouseDown={preventFocus} onClick={clearSelection}>
                  <FontAwesomeIcon icon={faXmark} />
                  <span>Clear</span>
                </button>
                
                <button className="imgv-selbtn imgv-selbtn-download" onMouseDown={preventFocus} onClick={handleDownloadSelected} disabled={isDownloading}>
                  <FontAwesomeIcon icon={faDownload} />
                  <span>{isDownloading ? 'Downloading...' : `Download (${selectedCount})`}</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Nav arrows */}
      {images.length > 1 && (
        <>
          <button className="imgv-nav imgv-nav-prev" onMouseDown={preventFocus} onClick={onPrev}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button className="imgv-nav imgv-nav-next" onMouseDown={preventFocus} onClick={onNext}>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </>
      )}

      {/* Image */}
      <div
        className="imgv-imgarea"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {isLoading && <div className="imgv-loader"><div className="imgv-spinner" /></div>}
        
        <img
          src={currentImage}
          alt={`${currentIndex + 1}`}
          className="imgv-img"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            opacity: isLoading ? 0 : 1,
          }}
          onLoad={() => setIsLoading(false)}
          onError={() => setIsLoading(false)}
          draggable={false}
        />

        {isSelectionMode && (
          <button
            className={`imgv-check ${isCurrentSelected ? 'imgv-check-active' : ''}`}
            onMouseDown={preventFocus}
            onClick={(e) => {
              e.stopPropagation();
              toggleSelection(currentIndex);
            }}
          >
            {isCurrentSelected && <FontAwesomeIcon icon={faCheck} />}
          </button>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="imgv-thumbs">
          {images.map((img, idx) => {
            const isSelected = selectedImages.has(idx);
            const isActive = idx === currentIndex;
            
            return (
              <button
                key={idx}
                className={`imgv-thumb ${isActive ? 'imgv-thumb-active' : ''} ${isSelectionMode && isSelected ? 'imgv-thumb-selected' : ''}`}
                onMouseDown={preventFocus}
                onClick={() => {
                  if (isSelectionMode) {
                    toggleSelection(idx);
                  } else {
                    goToImage(idx);
                  }
                }}
              >
                <img src={img} alt={`${idx + 1}`} draggable={false} />
                {isSelectionMode && (
                  <span className={`imgv-thumbcheck ${isSelected ? 'imgv-thumbcheck-active' : ''}`}>
                    {isSelected && <FontAwesomeIcon icon={faCheck} />}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ImageViewer;
